import { Alert, AlertTitle, Box, Typography } from "@mui/material"
import { AddLocationAltOutlined, VerifiedOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { FC } from "react"
import React from "react"

import { Button } from "@/components/common/Button"
import { CertificationDialogType } from "@/screens/Companies/edit/components/certification/types"

interface EmptyStateProps {
  hasNoLocations: boolean
  setDialogOpen: (open: CertificationDialogType) => void
}

const EmptyState: FC<EmptyStateProps> = ({ hasNoLocations, setDialogOpen }) => {
  const navigate = useNavigate()

  const handleTabChange = () => {
    void navigate({
      to: "/app/companies/$companyId/edit/locations",
      search: (old: Record<string, string>) => old,
      replace: true,
    })
  }
  return (
    <Box className='w-full rounded-md border border-solid border-gray-400 p-6'>
      <Typography variant='h5' className='text-gray-800'>
        Certifications
      </Typography>

      {hasNoLocations && (
        <Alert
          icon={<WarningOutlined color='primary' />}
          severity='info'
          variant='filled'
          className='mt-6'
          classes={{
            message: "w-full flex flex-row justify-between",
          }}
        >
          <Box>
            <AlertTitle className='font-normal leading-7'>
              At least one active location is required in order to add certifications.
            </AlertTitle>
            <Box className='w-full font-thin'>
              Click on the “Add Location” button, add your first location, and then return to Certifications to be able
              start adding them.
            </Box>
          </Box>
          <Box className='flex items-center'>
            <Button size='small' appearance='outlined' onClick={handleTabChange}>
              <AddLocationAltOutlined />
              Add Location
            </Button>
          </Box>
        </Alert>
      )}

      <Alert
        icon={<WarningOutlined color='primary' className={`${hasNoLocations ? "text-gray-500" : ""}`} />}
        severity='info'
        variant='filled'
        className={`mt-6 ${hasNoLocations ? "bg-gray-200" : ""}`}
        classes={{
          message: "w-full flex flex-row justify-between",
        }}
      >
        <Box>
          <AlertTitle className={`font-normal leading-7 ${hasNoLocations ? "text-gray-600" : "text-gray-900"}`}>
            Start by adding certifications to one or multiple locations
          </AlertTitle>
          <Box className={`w-full font-thin ${hasNoLocations ? "text-gray-500" : "text-gray-600"}`}>
            Click on “Add Certification”, choose type and add information to one or multiple locations.
          </Box>
        </Box>
        <Box className='flex items-center'>
          <Button
            disabled={hasNoLocations}
            size='small'
            appearance='outlined'
            onClick={() => setDialogOpen(CertificationDialogType.ADD_CERTIFICATION)}
          >
            <VerifiedOutlined />
            Add Certification
          </Button>
        </Box>
      </Alert>
    </Box>
  )
}

export default EmptyState
